import { Controller } from "@hotwired/stimulus";

// Lets a merchant point at the part of a photo that has
// to survive every crop the app puts it through, and
// shows those crops updating as they do it (CAL-454).
//
// Named `photo-focal-point`, NOT `image-position`:
// that identifier is already taken by the grid reorder
// controller.
//
// "Center" is `Photo::FOCAL_POINT_CENTER`, handed over
// as a value rather than repeated here: it is also the
// column default, so a copy on this side could disagree
// with the database about what an un-framed photo is.
const STEP = 1;
const COARSE_STEP = 10;

// Arrow keys are the only way in without a pointer, so
// they move the same point the pointer does.
const ARROW_KEYS = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

const clamp = (value) => Math.round(Math.min(100, Math.max(0, value)));

export default class extends Controller {
  static targets = [
    "stage",
    "image",
    "marker",
    "preview",
    "xField",
    "yField",
    "xReadout",
    "yReadout",
  ];

  static values = { x: Number, y: Number, center: Number };

  connect() {
    document.addEventListener("turbo:morph", this.#afterMorph);
    this.#paint();
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.#afterMorph);
    this.#releasePointer();
  }

  // Stimulus fires these on connect, so the marker, the
  // fields and every preview start from the value the
  // server rendered. They fire again on every write, but
  // only on the microtask after it: values round-trip
  // through the element's attributes and a
  // MutationObserver. That is a whole frame late for
  // something dragged under a finger, so `#moveTo`
  // paints as well. These two are for the value
  // arriving from anywhere else.
  xValueChanged() {
    this.#paint();
  }

  yValueChanged() {
    this.#paint();
  }

  startDrag(event) {
    // Right-click and middle-click open menus and paste;
    // neither is a framing decision. Touch and pen
    // report no button at all.
    if (event.button > 0) return;

    this.#dragging = true;
    this.#capturePointer(event);
    this.#pointAt(event);

    // Stops the browser starting its own image drag,
    // which would swallow every `pointermove` after it.
    event.preventDefault();
  }

  drag(event) {
    if (!this.#dragging) return;

    this.#pointAt(event);
    event.preventDefault();
  }

  endDrag() {
    if (!this.#dragging) return;

    this.#dragging = false;
    this.#releasePointer();
  }

  nudge(event) {
    const direction = ARROW_KEYS[event.key];
    if (!direction) return;

    const step = event.shiftKey ? COARSE_STEP : STEP;

    this.#moveTo(
      this.xValue + direction[0] * step,
      this.yValue + direction[1] * step
    );

    // Otherwise the arrow keys scroll the page out from
    // under the photo being framed.
    event.preventDefault();
  }

  reset() {
    this.#moveTo(this.centerValue, this.centerValue);
  }

  #dragging = false;
  #capturedPointerId = null;

  // Re-paint rather than re-build: a morph replaces the
  // marker's inline style and the fields' values with
  // whatever the server last rendered.
  #afterMorph = () => {
    this.#dragging = false;
    this.#paint();
  };

  #pointAt(event) {
    const frame = this.#frame();
    if (!frame || !frame.width || !frame.height) return;

    this.#moveTo(
      ((event.clientX - frame.left) / frame.width) * 100,
      ((event.clientY - frame.top) / frame.height) * 100
    );
  }

  // Percentages are of the PICTURE, not of the box
  // around it: a portrait photo under a height cap
  // leaves gutters, and a click there has to land on
  // the nearest edge of the image, not 0%.
  #frame() {
    if (this.hasImageTarget) {
      return this.imageTarget.getBoundingClientRect();
    }

    if (this.hasStageTarget) {
      return this.stageTarget.getBoundingClientRect();
    }

    return null;
  }

  #moveTo(x, y) {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;

    // The column is `integer` with a 0..100 check
    // constraint, so anything else is a failed save.
    this.xValue = clamp(x);
    this.yValue = clamp(y);

    this.#paint();
  }

  #paint() {
    const x = this.xValue;
    const y = this.yValue;
    const position = `${x}% ${y}%`;

    if (this.hasMarkerTarget) {
      this.markerTarget.style.left = `${x}%`;
      this.markerTarget.style.top = `${y}%`;
    }

    if (this.hasXFieldTarget) this.xFieldTarget.value = x;
    if (this.hasYFieldTarget) this.yFieldTarget.value = y;

    if (this.hasXReadoutTarget) {
      this.xReadoutTarget.textContent = `${x}`;
    }

    if (this.hasYReadoutTarget) {
      this.yReadoutTarget.textContent = `${y}`;
    }

    this.previewTargets.forEach((preview) => {
      preview.style.objectPosition = position;
    });
  }

  // Keeps a drag alive once the pointer leaves the
  // photo, so the point follows to the edge instead of
  // sticking where it left. Absent in jsdom, and a
  // nicety rather than a requirement.
  #capturePointer(event) {
    if (!this.hasStageTarget) return;
    if (event.pointerId === undefined) return;
    if (typeof this.stageTarget.setPointerCapture !== "function") return;

    try {
      this.stageTarget.setPointerCapture(event.pointerId);
      this.#capturedPointerId = event.pointerId;
    } catch {
      this.#capturedPointerId = null;
    }
  }

  #releasePointer() {
    const pointerId = this.#capturedPointerId;
    this.#capturedPointerId = null;

    if (pointerId === null) return;
    if (!this.hasStageTarget) return;
    if (typeof this.stageTarget.releasePointerCapture !== "function") return;

    try {
      this.stageTarget.releasePointerCapture(pointerId);
    } catch {
      // The pointer is already gone; nothing to release.
    }
  }
}
